import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import { services } from "@/data/services";
import { praktijkvoorbeelden } from "@/data/praktijkvoorbeelden";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
// framer-motion verwijderd uit Header (staat op elke pagina, ook homepage).
// Dropdowns/mobiel menu gebruiken nu `animate-fade-in` bij tonen; exit-animatie vervalt.
import logoRinqel from "@/assets/logo-rinqel.png";

// Direct links for items that have their own subpage
const itemPageLinks: Record<string, string> = {
  "Rinqel VDSL": "/diensten/internet/vdsl",
  "Managed Cloud Only Werkplek": "/diensten/werkplek/cloud-only",
  "Managed Werkplek": "/diensten/werkplek/managed",
  "Managed Kiosk Werkplek": "/diensten/werkplek/kiosk",
  "Managed Mobile Device Only": "/diensten/werkplek/mobile-device",
  "Losse Mailbox": "/diensten/werkplek/exchange-online",
  "Sim Only": "/diensten/mobiel",
  "Vast-mobiel integratie": "/diensten/mobiel",
  "Groepsbundels": "/diensten/mobiel",
  "Data-only simkaarten": "/diensten/mobiel",
  "VoIP Centrale": "/diensten/telefonie",
  "VoIP Aansluiting": "/diensten/telefonie",
  "Bellen met Teams": "/diensten/telefonie",
  "Softphone / App bellen": "/diensten/telefonie",
  "VAMO – Vast-Mobiel Integratie": "/diensten/telefonie",
  "CRM & ERP koppelingen": "/diensten/telefonie",
};

const toMenuItem = (p: (typeof praktijkvoorbeelden)[number]) => ({
  name: p.title,
  link: `/praktijkvoorbeelden/${p.slug}`,
  icon: p.icon,
});
const praktijkItems = praktijkvoorbeelden.filter((p) => p.category === "algemeen").map(toMenuItem);
const zorgItems = praktijkvoorbeelden.filter((p) => p.category === "zorg").map(toMenuItem);

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [praktijkOpen, setPraktijkOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const praktijkTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setServicesOpen(false);
    }, 150);
  };

  const handlePraktijkEnter = () => {
    if (praktijkTimeout.current) {
      clearTimeout(praktijkTimeout.current);
      praktijkTimeout.current = null;
    }
    setPraktijkOpen(true);
  };

  const handlePraktijkLeave = () => {
    praktijkTimeout.current = setTimeout(() => {
      setPraktijkOpen(false);
    }, 150);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img loading="eager" decoding="async" fetchPriority="high" src={logoRinqel} alt="Rinqel logo" width={256} height={256} className="h-9 w-auto object-contain md:h-10" />
          <span className="text-2xl font-bold tracking-tight">
            <span className="text-gradient">Rinqel</span>
            <span className="text-muted-foreground">.it</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {/* Diensten dropdown */}
          <div
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground">
              Diensten
              <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>

            <>
              {servicesOpen && (
                <div className="fixed left-0 right-0 top-[64px] z-50 md:top-[80px] animate-fade-in">
                  <div className="border-b border-border bg-card shadow-lg">
                    <div className="container py-8">
                      <div className="grid grid-cols-4 gap-x-10 gap-y-6">
                        {services.map((service) => (
                          <div key={service.slug}>
                            <Link
                              to={`/diensten/${service.slug}`}
                              className="mb-3 inline-block text-sm font-bold text-primary hover:underline"
                              onClick={() => setServicesOpen(false)}
                            >
                              {service.title}
                            </Link>
                            <ul className="space-y-1.5">
                              {service.items.map((item) => {
                                const itemLink = itemPageLinks[item.name] || `/diensten/${service.slug}`;
                                return (
                                  <li key={item.name}>
                                    <Link
                                      to={itemLink}
                                      className="text-sm text-foreground/80 transition-colors hover:text-primary"
                                      onClick={() => setServicesOpen(false)}
                                    >
                                      {item.name}
                                    </Link>
                                  </li>
                                );
                              })}
                              {service.slug === "werkplek" && (
                                <li className="border-t border-border pt-1.5 mt-1.5">
                                  <Link
                                    to="/diensten/werkplek/overstap"
                                    className="text-sm font-medium text-foreground/80 transition-colors hover:text-primary"
                                    onClick={() => setServicesOpen(false)}
                                  >
                                    Hoe werkt de overstap?
                                  </Link>
                                </li>
                              )}
                              {service.slug === "hardware" && (
                                <li className="border-t border-border pt-1.5 mt-1.5">
                                  <Link
                                    to="/diensten/hardware/inruilen"
                                    className="text-sm font-medium text-foreground/80 transition-colors hover:text-primary"
                                    onClick={() => setServicesOpen(false)}
                                  >
                                    Oude devices inruilen
                                  </Link>
                                </li>
                              )}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          </div>

          {/* Praktijkvoorbeelden mega-menu */}
          <div
            className="relative"
            onMouseEnter={handlePraktijkEnter}
            onMouseLeave={handlePraktijkLeave}
          >
            <button className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground">
              Praktijkvoorbeelden
              <ChevronDown className={`h-4 w-4 transition-transform ${praktijkOpen ? "rotate-180" : ""}`} />
            </button>

            <>
              {praktijkOpen && (
                <div className="fixed left-0 right-0 top-[64px] z-50 md:top-[80px] animate-fade-in">
                  <div className="border-b border-border bg-card shadow-lg">
                    <div className="container flex flex-wrap gap-16 py-8">
                      <div>
                        <Link
                          to="/praktijkvoorbeelden"
                          className="mb-3 inline-block text-sm font-bold text-primary hover:underline"
                          onClick={() => setPraktijkOpen(false)}
                        >
                          Praktijkvoorbeelden
                        </Link>
                        <ul className="space-y-1.5">
                          {praktijkItems.map((item) => {
                            const Icon = item.icon;
                            return (
                              <li key={item.link}>
                                <Link
                                  to={item.link}
                                  className="inline-flex items-center gap-2 text-sm text-foreground/80 transition-colors hover:text-primary"
                                  onClick={() => setPraktijkOpen(false)}
                                >
                                  <Icon className="h-4 w-4 text-primary" />
                                  {item.name}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      <div>
                        <Link
                          to="/praktijkvoorbeelden#zorg"
                          className="mb-3 inline-block text-sm font-bold text-primary hover:underline"
                          onClick={() => setPraktijkOpen(false)}
                        >
                          Zorg
                        </Link>
                        <ul className="space-y-1.5">
                          {zorgItems.map((item) => {
                            const Icon = item.icon;
                            return (
                              <li key={item.link}>
                                <Link
                                  to={item.link}
                                  className="inline-flex items-center gap-2 text-sm text-foreground/80 transition-colors hover:text-primary"
                                  onClick={() => setPraktijkOpen(false)}
                                >
                                  <Icon className="h-4 w-4 text-primary" />
                                  {item.name}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          </div>

          <Link
            to="/configurator"
            className="rounded-lg px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
          >
            Prijzen
          </Link>
          <Link
            to="/klanten"
            className="rounded-lg px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
          >
            Klanten
          </Link>
          <Link
            to="/faq"
            className="rounded-lg px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
          >
            Q&amp;A
          </Link>
          <Link
            to="/contact"
            className="rounded-lg px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
          >
            Contact
          </Link>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-3 lg:flex">
          <Button variant="hero" size="lg" asChild>
            <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="header_desktop">Bereken je prijs</TrackedLink>
          </Button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="lg:hidden rounded-lg p-2 text-foreground hover:bg-accent"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Nav */}
      <>
        {mobileOpen && (
          <div className="overflow-hidden border-t border-border lg:hidden animate-fade-in">
            <div className="container space-y-1 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Diensten</p>
              {services.map((service) => {
                const Icon = service.icon;
                const isExpanded = mobileExpanded === service.slug;
                const hasSubItems = service.items.length > 0;
                return (
                  <div key={service.slug}>
                    <div className="flex items-center rounded-lg transition-colors hover:bg-accent">
                      <Link
                        to={`/diensten/${service.slug}`}
                        className="flex flex-1 items-center gap-3 px-3 py-3 text-sm font-medium"
                        onClick={() => setMobileOpen(false)}
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        {service.title}
                      </Link>
                      {hasSubItems && (
                        <button
                          onClick={() => setMobileExpanded(isExpanded ? null : service.slug)}
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-r-lg text-muted-foreground hover:text-foreground active:bg-accent/80"
                          aria-label={`${isExpanded ? "Sluit" : "Open"} submenu ${service.title}`}
                        >
                          <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
                        </button>
                      )}
                    </div>
                    <>
                      {isExpanded && hasSubItems && (
                        <div className="overflow-hidden animate-fade-in">
                          <div className="ml-10 space-y-0.5 pb-2 pt-1">
                            {service.items.map((item) => {
                              const itemLink = itemPageLinks[item.name] || `/diensten/${service.slug}`;
                              return (
                                <Link
                                  key={item.name}
                                  to={itemLink}
                                  className="block rounded-md px-3 py-2.5 text-sm text-foreground/70 transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              );
                            })}
                            {service.slug === "werkplek" && (
                              <Link
                                to="/diensten/werkplek/overstap"
                                className="block rounded-md border-t border-border/50 mt-1 pt-1.5 px-3 py-2.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
                                onClick={() => setMobileOpen(false)}
                              >
                                Hoe werkt de overstap?
                              </Link>
                            )}
                            {service.slug === "hardware" && (
                              <Link
                                to="/diensten/hardware/inruilen"
                                className="block rounded-md border-t border-border/50 mt-1 pt-1.5 px-3 py-2.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
                                onClick={() => setMobileOpen(false)}
                              >
                                Oude devices inruilen
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  </div>
                );
              })}

              <div className="my-3 border-t border-border" />

              {/* Praktijkvoorbeelden mobile section */}
              <div>
                <div className="flex items-center rounded-lg transition-colors hover:bg-accent">
                  <Link
                    to="/praktijkvoorbeelden"
                    className="flex flex-1 items-center gap-3 px-3 py-3 text-sm font-medium"
                    onClick={() => setMobileOpen(false)}
                  >
                    Praktijkvoorbeelden
                  </Link>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === "praktijk" ? null : "praktijk")}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-r-lg text-muted-foreground hover:text-foreground active:bg-accent/80"
                    aria-label={`${mobileExpanded === "praktijk" ? "Sluit" : "Open"} submenu Praktijkvoorbeelden`}
                  >
                    <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${mobileExpanded === "praktijk" ? "rotate-90" : ""}`} />
                  </button>
                </div>
                <>
                  {mobileExpanded === "praktijk" && (
                    <div className="overflow-hidden animate-fade-in">
                      <div className="ml-10 space-y-0.5 pb-2 pt-1">
                        {praktijkItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.link}
                              to={item.link}
                              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground/70 transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
                              onClick={() => setMobileOpen(false)}
                            >
                              <Icon className="h-4 w-4 text-primary" />
                              {item.name}
                            </Link>
                          );
                        })}
                        <p className="px-3 pb-1 pt-3 text-xs font-bold uppercase tracking-wider text-primary">Zorg</p>
                        {zorgItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.link}
                              to={item.link}
                              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground/70 transition-colors hover:bg-accent hover:text-foreground active:bg-accent/80"
                              onClick={() => setMobileOpen(false)}
                            >
                              <Icon className="h-4 w-4 text-primary" />
                              {item.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              </div>

              <div className="my-3 border-t border-border" />
              <Link
                to="/configurator"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
                onClick={() => setMobileOpen(false)}
              >
                Prijzen
              </Link>
              <Link
                to="/klanten"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
                onClick={() => setMobileOpen(false)}
              >
                Klanten
              </Link>
              <Link
                to="/faq"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
                onClick={() => setMobileOpen(false)}
              >
                Q&amp;A
              </Link>
              <Link
                to="/contact"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent"
                onClick={() => setMobileOpen(false)}
              >
                Contact
              </Link>
              <div className="pt-3">
                <Button variant="hero" className="w-full" asChild>
                  <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="header_mobile" onClick={() => setMobileOpen(false)}>
                    Bereken je prijs
                  </TrackedLink>
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    </header>
  );
};

export default Header;
